import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'react-covideo-common';
import { theme } from 'lib/style';
import { MdMic, MdPause, MdPlayArrow, MdStop } from 'react-icons/md';
import { MdRefresh } from 'react-icons/md';
import { BsRecordCircle } from 'react-icons/bs';
import { useMediaRecorder } from 'lib/hooks/useMediaRecorder';
import { useHistory } from 'react-router';
import { toMMSS } from 'lib/utils/functions';
import { ModalPrompt } from 'lib/components/modal';
import { errorToast } from 'lib/components/toasts/error';
import { useCDK } from 'lib/hooks/useCdk';
import {
  DEFAULT_SETTINGS,
  RecordingStatus,
  RecordingStatusType,
} from './constants';
import {
  Wrapper,
  Audio,
  RecordingProgress,
  ButtonsWrapper,
  RecordingInfo,
  MicIconWrapper,
  CurrentTime,
  ProgressBar,
  Slider,
  Done,
} from './styles';
import { Duration } from 'app/pages/meetings/styles';
import { StyleObject } from './types';

type Props = {
  onChangeRecordingValid: (valid: boolean) => void;
  onRecordingUrlGeneration: (url: string) => void;
  onRetakeRecording?: () => void;
  disabled?: boolean;
  extendedStyles?: StyleObject;
};

export const VoiceRecorder = ({
  onChangeRecordingValid,
  onRecordingUrlGeneration,
  onRetakeRecording,
  disabled = false,
  extendedStyles,
}: Props) => {
  const { destructiveToSecondary, redToSecondary } = useCDK();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatusType>(
    RecordingStatus.NOT_RECORDING
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showRetakePrompt, setShowRetakePrompt] = useState(false);
  const history = useHistory();

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const settings = {
    audioSource: JSON.parse(
      localStorage.getItem('record_settings') ||
        JSON.stringify(DEFAULT_SETTINGS)
    ).audioSource as any,
  };

  let audio: any;
  try {
    audio = { deviceId: settings.audioSource.value };
  } catch (e) {}
  if (!settings || !settings.audioSource || !settings.audioSource.value) {
    audio = false;
  }

  const {
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    mediaBlobUrl,
  } = useMediaRecorder({
    audio,
  });

  if (!audio || error) {
    errorToast({
      title: 'Please enable audio in your recording settings and try again!',
    });
    history.push('/record/home');
  }

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const startTimer = () => {
    stopTimer();
    timerIntervalRef.current = setInterval(() => {
      setCurrentTime(prevTime => prevTime + 1);
    }, 1000);
  };

  const startRecordingClick = async () => {
    onRecordingUrlGeneration('');
    setCurrentTime(0);
    setDuration(0);
    startRecording();
    setRecordingStatus(RecordingStatus.RECORDING);
    startTimer();
  };

  const pauseRecordingClick = () => {
    pauseRecording();
    setRecordingStatus(RecordingStatus.RECORDING_PAUSED);
    stopTimer();
  };

  const resumeRecordingClick = () => {
    resumeRecording();
    setRecordingStatus(RecordingStatus.RECORDING);
    startTimer();
  };

  const stopRecordingClick = async () => {
    await stopRecording();
    setRecordingStatus(RecordingStatus.FINISHED);
    stopTimer();

    setDuration(currentTime);
    setCurrentTime(0);
  };

  const retakeButtonClick = () => {
    setShowRetakePrompt(true);
  };

  const retakeRecording = async () => {
    setShowRetakePrompt(false);

    const currentStatus = recordingStatus;
    setRecordingStatus(RecordingStatus.NOT_RECORDING);

    if (onRetakeRecording) onRetakeRecording();
    onChangeRecordingValid(false);

    if (currentStatus === RecordingStatus.RECORDING_PAUSED) {
      await stopRecording();
    }

    stopTimer();

    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.currentTime = 0;
      audioPlayer.src = '';
    }

    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  };

  const playAudio = () => {
    if (!audioPlayer) return;
    audioPlayer.play();
  };

  const pauseAudio = () => {
    if (!audioPlayer) return;
    audioPlayer.pause();
  };

  const seekAudio = (newTime: number) => {
    if (!audioPlayer) return;
    audioPlayer.currentTime = newTime;
    setCurrentTime(newTime);
  };

  useEffect(() => {
    return () => {
      stopTimer();
      //@ts-ignore
      const streams = [...window.streams];
      //@ts-ignore
      streams.filter(Boolean).forEach(s => stopStream(s));
      //@ts-ignore
      window.streams = [];
    };
  }, []);

  const stopStream = (s: any) => {
    try {
      s.getTracks().forEach((t: any) => t.stop());
    } catch (e) {}
  };

  useEffect(() => {
    if (audioRef.current) {
      setAudioPlayer(audioRef.current);
    }
  }, [audioRef]);

  useEffect(() => {
    if (!audioPlayer) return;

    const handleTimeUpdate = () => setCurrentTime(audioPlayer.currentTime);

    const handleLoadedMetadata = () => {
      if (audioPlayer && isFinite(audioPlayer.duration)) {
        setDuration(audioPlayer.duration);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audioPlayer.addEventListener('timeupdate', handleTimeUpdate);
    audioPlayer.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioPlayer.addEventListener('play', handlePlay);
    audioPlayer.addEventListener('pause', handlePause);
    audioPlayer.addEventListener('ended', handleEnded);

    return () => {
      audioPlayer.removeEventListener('timeupdate', handleTimeUpdate);
      audioPlayer.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioPlayer.removeEventListener('play', handlePlay);
      audioPlayer.removeEventListener('pause', handlePause);
      audioPlayer.removeEventListener('ended', handleEnded);
    };
  }, [audioPlayer]);

  useEffect(() => {
    onChangeRecordingValid(
      !!mediaBlobUrl && recordingStatus === RecordingStatus.FINISHED
    );
  }, [recordingStatus, mediaBlobUrl, onChangeRecordingValid]);

  useEffect(() => {
    if (
      !mediaBlobUrl ||
      !audioRef.current ||
      recordingStatus !== RecordingStatus.FINISHED
    ) {
      return;
    }

    if (audioRef.current.src !== mediaBlobUrl) {
      audioRef.current.src = mediaBlobUrl;
      audioRef.current.load();

      onRecordingUrlGeneration(mediaBlobUrl);
    }
  }, [mediaBlobUrl, recordingStatus, onRecordingUrlGeneration]);

  const isFinished = recordingStatus === RecordingStatus.FINISHED;
  const isRecording = recordingStatus === RecordingStatus.RECORDING;

  const currentPosition =
    !duration || duration === 0
      ? 0
      : (parseFloat(currentTime.toString()) / parseFloat(duration.toString())) *
        100;

  const updatePosition = (e: any) => {
    const newPosition = e.target.value.toString();
    const time = duration * (newPosition / 100);
    seekAudio(time);
  };

  const currentTimeMMSS = toMMSS(currentTime, 0);
  const durationMMSS = toMMSS(duration, 0);

  return (
    <Wrapper style={extendedStyles?.wrapper} disabled={disabled}>
      <Audio ref={audioRef}>
        <source src={mediaBlobUrl || ''} />
      </Audio>
      <ButtonsWrapper>
        {recordingStatus === RecordingStatus.NOT_RECORDING && (
          <Button
            variant={redToSecondary}
            icon={<BsRecordCircle size={12} />}
            text={'Record'}
            onClick={() => startRecordingClick()}
          />
        )}

        {recordingStatus === RecordingStatus.RECORDING && (
          <>
            <Button
              icon={<MdPause />}
              text={'Pause'}
              onClick={() => pauseRecordingClick()}
              variant='secondary'
            />
            <Button
              icon={<MdStop />}
              text={'Stop'}
              onClick={() => stopRecordingClick()}
              variant='secondary'
            />
          </>
        )}

        {recordingStatus === RecordingStatus.RECORDING_PAUSED && (
          <>
            <Button
              icon={<MdPlayArrow />}
              text={'Resume'}
              onClick={() => resumeRecordingClick()}
              variant='secondary'
            />
            <Button
              icon={<MdStop />}
              text={'Stop'}
              onClick={() => stopRecordingClick()}
              variant='secondary'
            />
          </>
        )}

        {recordingStatus === RecordingStatus.FINISHED && !isPlaying && (
          <Button
            icon={<MdPlayArrow />}
            onClick={() => playAudio()}
            variant='secondary'
            text={'Play'}
          />
        )}
        {recordingStatus === RecordingStatus.FINISHED && isPlaying && (
          <Button
            icon={<MdPause />}
            onClick={() => pauseAudio()}
            variant='secondary'
            text={'Pause'}
          />
        )}
      </ButtonsWrapper>
      <RecordingProgress
        disabled={!isFinished}
        title={!isFinished ? 'Controls disabled until finished.' : ''}
      >
        <RecordingInfo>
          <MicIconWrapper>
            <MdMic
              size={22}
              color={isRecording ? theme.palette.red100 : theme.palette.gray60}
            />
          </MicIconWrapper>
          <CurrentTime>{currentTimeMMSS}</CurrentTime>
        </RecordingInfo>
        <ProgressBar>
          <Slider
            disabled={!isFinished}
            type='range'
            min='0'
            max='100'
            value={currentPosition}
            onChange={e => updatePosition(e)}
            step='1'
          />
          <Done
            background={
              isRecording ? theme.palette.red100 : theme.palette.blue100
            }
            position={currentPosition}
          />
        </ProgressBar>
        <Duration>{durationMMSS}</Duration>
      </RecordingProgress>

      {(recordingStatus === RecordingStatus.RECORDING_PAUSED ||
        recordingStatus === RecordingStatus.FINISHED) && (
        <ButtonsWrapper>
          <Button
            variant={destructiveToSecondary}
            icon={<MdRefresh />}
            text={'Start Over'}
            onClick={() => retakeButtonClick()}
          />
        </ButtonsWrapper>
      )}

      {showRetakePrompt && (
        <ModalPrompt
          title={'Start Over?'}
          content={<>Your recording will be erased.</>}
          secondaryButtonText={'Go Back'}
          primaryButtonText={'Start Over'}
          primaryButtonType={'destructive'}
          handleSubmit={() => retakeRecording()}
          handleModalClose={() => setShowRetakePrompt(false)}
        />
      )}
    </Wrapper>
  );
};
